import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import { useTranslation } from 'react-i18next';
import { Permissions } from '../../config';
import Roles from 'config/Roles';
import useSWR, { mutate } from 'swr';
import debounce from 'lodash/debounce';
import { useInView } from 'react-intersection-observer';

// material-ui icons
import { createUseStyles } from 'react-jss';
import { Add } from '@mui/icons-material';

// core components
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaFilter from 'components/SparaFilter/SparaFilter';
import CompanyCard from 'components/CompanyCard/CompanyCard';
import SparaLoader from 'components/SparaLoader/SparaLoader';

import { companyApi, fetcher } from 'lib/api';

import styles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaTitle from 'components/SparaTitle/SparaTitle';

const style = {
    ...styles,
    cardsContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: '16px',
        padding: '16px',
    },
    noResults: {
        textAlign: 'center',
        padding: '32px',
        color: 'rgba(0,0,0,0.5)',
        fontSize: '16px',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--spara-overlay, rgba(0, 0, 0, 0.45))',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '32px',
        zIndex: 2,
    },
    stickyFilterContainer: {
        position: 'sticky',
        top: '50px',
        backgroundColor: 'var(--spara-chrome, white)',
        zIndex: 99,
        padding: '8px 16px 16px 16px',
        boxShadow: 'var(--spara-shadow, 0 2px 4px rgba(0, 0, 0, 0.05))',
        marginBottom: '0',
    },
};

const useStyles = createUseStyles(style);

const Companies = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    const navigate = useNavigate();
    const location = useLocation();
    const [errorDialog, setErrorDialog] = useState({ open: false, title: '', message: '' });
    const [statusDialog, setStatusDialog] = useState({ open: false, company: null, status: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, companyId: null });
    const [inputValue, setInputValue] = useState('');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({
        type: null,
        status: null,
    });
    const [page, setPage] = useState(1);
    const [pageLimit] = useState(50);
    const [isActiveLoadMore, setIsActiveLoadMore] = useState(true);
    const [allCompanies, setAllCompanies] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const localUser = JSON.parse(localStorage.getItem('user'));
    const userPermission = localUser.permission;
    const userRoles = localUser.roles;
    const isSuperAdmin = userRoles?.includes(Roles.SUPER_ADMIN);
    const isCompanyAdmin = userPermission.includes(Permissions.CU_COMPANY_UPDATE);

    // Setup intersection observer for infinite scroll
    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        rootMargin: '100px',
    });

    const updateURL = useCallback(
        (newFilters, newSearch, newPage) => {
            const params = new URLSearchParams();
            if (newFilters.type) params.set('type', newFilters.type);
            if (newFilters.status) params.set('status', newFilters.status);
            if (newSearch) params.set('search', newSearch);
            if (newPage > 1) params.set('page', newPage);

            navigate({ search: params.toString() }, { replace: true });
        },
        [navigate]
    );

    // Initialize filters from URL on mount only
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const type = params.get('type');
        const status = params.get('status');
        const searchParam = params.get('search');
        const pageParam = params.get('page');

        const initialFilters = {
            type: type || null,
            status: status || null,
        };
        setFilters(initialFilters);
        if (searchParam) {
            setSearch(searchParam);
            setInputValue(searchParam);
        }
        if (pageParam) setPage(parseInt(pageParam));
    }, []); // Mount only - URL params read once on initial load

    // SWR hook for fetching companies
    const { data: companies, error, isLoading, isValidating, isMutating } = useSWR(
        companyApi.list({
            page,
            limit: pageLimit,
            ...(search && { search }),
            ...(filters.type && { type: filters.type }),
            ...(filters.status && { status: filters.status }),
            'my-companies': isCompanyAdmin ? true : null,
        }),
        fetcher,
        {
            keepPreviousData: true,
            revalidate: false, // Avoid revalidating here, let the filter/search handlers do it
        }
    );

    // Redirect directly only if user has access to exactly 1 company and no search/filters are active
    useEffect(() => {
        const hasActiveSearch = !!search;
        const hasActiveFilters = !!(filters.type || filters.status);
        if (
            !hasActiveSearch &&
            !hasActiveFilters &&
            companies?.count === 1 &&
            companies?.results?.length === 1
        ) {
            navigate(`/company/${companies.results[0].id}`, { replace: true });
        }
    }, [companies, navigate, search, filters]);

    // Scroll to top when filters change
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [filters]);

    // Scroll to top when search changes
    useEffect(() => {
        window.scrollTo(0, 0);
    }, [search]);

    // Callback to check if we should load more
    const shouldLoadMore = useCallback(() => {
        if (
            !isLoading &&
            !isValidating &&
            allCompanies.length > 0 &&
            allCompanies.length < totalCount &&
            isActiveLoadMore
        ) {
            const newPage = page + 1;
            setPage(newPage);
            updateURL(filters, search, newPage);
        }
    }, [isLoading, isValidating, allCompanies.length, totalCount, isActiveLoadMore, page, filters, search, updateURL]);

    // Handle scroll events as backup
    React.useEffect(() => {
        const handleScroll = () => {
            const scrollHeight = document.documentElement.scrollHeight;
            const scrollTop = window.scrollY;
            const clientHeight = window.innerHeight;

            // If we're close to bottom (within 200px), try to load more
            if (scrollHeight - scrollTop - clientHeight < 200) {
                shouldLoadMore();
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [shouldLoadMore]);

    // Handle intersection observer
    React.useEffect(() => {
        if (inView) {
            shouldLoadMore();
        }
    }, [inView, shouldLoadMore]);

    // Update local state when new data arrives
    React.useEffect(() => {
        if (companies?.results) {
            if (page === 1) {
                // Reset data when filters change
                setAllCompanies(companies.results);
            } else {
                // Append new data for infinite scroll
                setAllCompanies((prev) => {
                    // Forðumst að bæta við sömu gögnum aftur
                    const newIds = new Set(companies.results.map((company) => company.id));
                    const filteredPrev = prev.filter((company) => !newIds.has(company.id));
                    return [...filteredPrev, ...companies.results];
                });
            }
            setTotalCount(companies.count);

            // Disable infinite scroll if we've loaded all items
            if (companies.results.length < pageLimit) {
                setIsActiveLoadMore(false);
            }
        }
    }, [companies, page, pageLimit]);

    // Scroll to top when filters/search cause page 1 data to load
    useEffect(() => {
        if (page === 1) {
            // Use setTimeout to ensure scroll happens after potential re-renders
            // Try setting scrollTop directly on document elements
            setTimeout(() => {
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0; // For older browsers/compatibility
            }, 0);
        }
    }, [allCompanies, page]); // Trigger when data changes AND page is 1

    // Reset page and data when filters change
    const handleFilterClick = (filterId, value) => {
        const newFilters = {
            ...filters,
            [filterId]: filters[filterId] === value ? null : value,
        };
        setFilters(newFilters);
        setPage(1);
        setIsActiveLoadMore(true);
        updateURL(newFilters, search, 1);
        mutate(
            companyApi.list({
                page: 1,
                limit: pageLimit,
                ...(search && { search }),
                ...(newFilters.type && { type: newFilters.type }),
                ...(newFilters.status && { status: newFilters.status }),
                'my-companies': isCompanyAdmin ? true : null,
            })
        );
    };

    const debouncedSearch = React.useCallback(
        debounce((value) => {
            setSearch(value);
            setPage(1);
            setIsActiveLoadMore(true);
            updateURL(filters, value, 1);
            mutate(
                companyApi.list({
                    page: 1,
                    limit: pageLimit,
                    ...(value && { search: value }),
                    ...(filters.type && { type: filters.type }),
                    ...(filters.status && { status: filters.status }),
                    'my-companies': isCompanyAdmin ? true : null,
                })
            );
        }, 250),
        [filters, pageLimit, updateURL]
    );

    const handleSearchChange = (event) => {
        const value = event.target.value || '';
        setInputValue(value);
        debouncedSearch(value);
    };

    const onchangeCompanyStatus = (company, status) => {
        setStatusDialog({ open: true, company, status });
    };

    const disabledCompanySuccess = async () => {
        const { company, status } = statusDialog;
        setStatusDialog({ open: false, company: null, status: null });
        try {
            await fetcher(companyApi.changeStatus, 'POST', {
                company_id: company,
                status: status,
            });

            mutate(
                companyApi.list({
                    page,
                    limit: pageLimit,
                    ...(search && { search }),
                    ...(filters.type && { type: filters.type }),
                    ...(filters.status && { status: filters.status }),
                    'my-companies': isCompanyAdmin ? true : null,
                })
            );
        } catch (error) {
            const res = error.response?.data;
            const message = res?.results ? res.results[Object.keys(res.results)[0]] : '';
            setErrorDialog({ open: true, title: 'Villa!', message: (res?.message || 'Error') + '. ' + message });
        }
    };

    const deleteCompanyHandler = async () => {
        const { companyId } = deleteDialog;
        setDeleteDialog({ open: false, companyId: null });
        try {
            await fetcher(companyApi.delete(companyId), 'DELETE');

            mutate(
                companyApi.list({
                    page,
                    limit: pageLimit,
                    ...(search && { search }),
                    ...(filters.type && { type: filters.type }),
                    ...(filters.status && { status: filters.status }),
                    'my-companies': isCompanyAdmin ? true : null,
                })
            );
        } catch (error) {
            setErrorDialog({ open: true, title: 'Villa við að eyða fyrirtæki', message: error.response?.data?.message || 'Villa kom upp' });
        }
    };

    const filterGroups = isSuperAdmin ? [
        // Tegund hópur
        [
            {
                id: 'type',
                label: t('filter.premium'),
                value: 'premium',
                color: '#ffc107',
            },
            {
                id: 'type',
                label: t('filter.regular'),
                value: 'regular',
                color: '#4caf50',
            },
        ],
        // Staða hópur
        [
            {
                id: 'status',
                label: t('filter.active'),
                value: 'active',
                color: '#4caf50',
            },
            {
                id: 'status',
                label: t('filter.disabled'),
                value: 'disabled',
                color: '#ffa500',
            },
        ],
    ] : [];

    const handleClearAll = () => {
        // Clear all filters
        setFilters({
            type: null,
            status: null,
        });

        // Clear search
        setInputValue('');
        setSearch('');

        // Reset page
        setPage(1);
        setIsActiveLoadMore(true);

        // Update URL
        navigate({ search: '' }, { replace: true });

        // Revalidate data
        mutate(
            companyApi.list({
                page: 1,
                limit: pageLimit,
                'my-companies': isCompanyAdmin ? true : null,
            })
        );
    };

    if (error) return <div>Villa kom upp við að sækja gögn</div>;

    return (
        <>
            <ConfirmDialog
                open={errorDialog.open}
                type="danger"
                title={errorDialog.title}
                message={errorDialog.message}
                confirmText={t('ok', { defaultValue: 'Loka' })}
                onConfirm={() => setErrorDialog({ open: false, title: '', message: '' })}
            />
            <ConfirmDialog
                open={statusDialog.open}
                type={statusDialog.status === 'disable' ? 'warning' : 'success'}
                title={statusDialog.status === 'disable' ? 'Afvirkja fyrirtæki?' : 'Virkja fyrirtæki?'}
                message={
                    statusDialog.status === 'disable'
                        ? 'Þetta mun afvirkja fyrirtækið og slökkva á öllum tilboðum þess.'
                        : 'Þetta mun virkja fyrirtækið aftur.'
                }
                confirmText={t('yes')}
                cancelText={t('cancel')}
                onConfirm={disabledCompanySuccess}
                onCancel={() => setStatusDialog({ open: false, company: null, status: null })}
            />
            <ConfirmDialog
                open={deleteDialog.open}
                type="danger"
                title="Eyða fyrirtæki?"
                message="Þessi aðgerð er óafturkræf. Fyrirtækið verður fjarlægt."
                confirmText={t('yes')}
                cancelText={t('cancel')}
                onConfirm={deleteCompanyHandler}
                onCancel={() => setDeleteDialog({ open: false, companyId: null })}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('companies')}
                    rightComponent={
                        <>
                            {userPermission.includes(Permissions.COMPANY_CREATE) && (
                                <SparaButton
                                    color="primary"
                                    icon={Add}
                                    onClick={() => navigate('/add-company')}
                                >
                                    Nýtt fyrirtæki
                                </SparaButton>
                            )}
                        </>
                    }
                />
                <Card style={{ margin: '0px' }}>
                    {isSuperAdmin && (
                        <div className={classes.stickyFilterContainer}>
                            <SparaFilter
                                filters={filters}
                                onFilterClick={handleFilterClick}
                                onClearAll={handleClearAll}
                                search={inputValue}
                                onSearchChange={handleSearchChange}
                                filterGroups={filterGroups}
                                t={t}
                            />
                            {companies && (
                                <div
                                    style={{
                                        fontWeight: 'bold',
                                        fontSize: '16px',
                                        fontStyle: 'italic',
                                        color: '#007aaa',
                                        marginTop: '8px',
                                        marginLeft: '8px',
                                    }}
                                >
                                    {`${t('filter.showing')} ${allCompanies.length} ${t('filter.of')} ${
                                        companies.count
                                    } fyrirtækjum`}
                                </div>
                            )}
                        </div>
                    )}
                    <CardBody style={{ position: 'relative', paddingTop: isSuperAdmin ? '0' : undefined }}>
                        <GridItem></GridItem>
                        {isSuperAdmin && (isLoading || isMutating) && page === 1 && (
                            <div className={classes.loadingOverlay}>
                                <SparaLoader text={t('common.loading')} />
                            </div>
                        )}
                        {!allCompanies?.length ? (
                            <div className={classes.noResults}>
                                {t('no_results_with_current_filter')}
                            </div>
                        ) : (
                            <>
                                <div className={classes.cardsContainer}>
                                    {allCompanies.map((company) => (
                                        <CompanyCard
                                            key={company.id}
                                            company={company}
                                            onEdit={(company) => navigate(`/company/${company.id}`)}
                                            onStatusChange={
                                                isSuperAdmin ? onchangeCompanyStatus : undefined
                                            }
                                            onDelete={
                                                isSuperAdmin
                                                    ? (company) => setDeleteDialog({ open: true, companyId: company.id })
                                                    : undefined
                                            }
                                            t={t}
                                        />
                                    ))}
                                </div>
                                {/* Loading indicator for infinite scroll */}
                                {isLoading && page > 1 && (
                                    <div className={classes.noResults}>
                                        <SparaLoader text={t('common.loading_more')} />
                                    </div>
                                )}
                                {/* Intersection observer target */}
                                {isActiveLoadMore && allCompanies.length < totalCount && (
                                    <div
                                        ref={loadMoreRef}
                                        style={{
                                            height: '20px',
                                            margin: '20px 0',
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </>
    );
};

export default Companies;
